import { ROUTES } from 'src/router/const'
import { sessionStorageService } from 'src/services/app/storage.service'
import { AUTH_SCOPE } from 'src/services/consts/auth.const'
import { QUERY_KEYS } from 'src/services/consts/queries.const'
import { isBearerTokenExpired } from 'src/services/helpers/auth.helper'
import { hasValidGrants } from 'src/services/helpers/session.helper'

/** Matches `/link/:linkId` — param value is the API `link_code`. */
const LINK_INVITE_PATH_PATTERN = new RegExp(`^/${ROUTES.LINK_LOGIN}/([^/]+)/?$`)

/** @typedef {{ bearerToken: string, grants: Record<string, unknown>, email: string }} EmailAccessSession */
/** @typedef {{ bearerToken: string, grants: Record<string, unknown>, linkCode: string }} LinkAccessSession */

export const sessionAccessService = {
  getAccess,
  readAccess,
  getReloginCredential,
  persistAccess,
  clearAccessCredentials,
  clearAccess,
  getBearerToken,
}

/**
 * Returns a valid session blob or null (expired / missing grants / bad token).
 *
 * @param {(typeof AUTH_SCOPE)[keyof typeof AUTH_SCOPE]} authScope
 * @returns {EmailAccessSession | LinkAccessSession | null}
 */
function getAccess(authScope) {
  const session = readStoredSession(authScope)
  if (!session || !isValidAccessSession(session)) {
    return null
  }
  return session
}

/**
 * Session reuse when the relogin credential matches (email or link code).
 *
 * @param {(typeof AUTH_SCOPE)[keyof typeof AUTH_SCOPE]} authScope
 * @param {string} credential
 * @returns {EmailAccessSession | LinkAccessSession | null}
 */
function readAccess(authScope, credential) {
  const trimmedCredential = credential?.trim()
  if (!trimmedCredential) return null

  const session = getAccess(authScope)
  if (!session) return null

  if (authScope === AUTH_SCOPE.LINK) {
    return session.linkCode === trimmedCredential ? session : null
  }

  return session.email === trimmedCredential ? session : null
}

/**
 * Email or link code for silent re-login, even when bearer/grants were cleared.
 *
 * @param {(typeof AUTH_SCOPE)[keyof typeof AUTH_SCOPE]} authScope
 * @returns {string | null}
 */
function getReloginCredential(authScope) {
  if (authScope === AUTH_SCOPE.LINK) {
    const session = readStoredSession(authScope)
    const fromSession =
      typeof session?.linkCode === 'string' && session.linkCode.trim() !== ''
        ? session.linkCode.trim()
        : null
    return fromSession ?? getLinkCodeFromLocation()
  }

  const session = readStoredSession(authScope)
  if (!session) return null

  return typeof session.email === 'string' && session.email.trim() !== ''
    ? session.email.trim()
    : null
}

/**
 * Reads `link_code` from the invite route param when sessionStorage is missing or corrupt.
 *
 * @returns {string | null}
 */
function getLinkCodeFromLocation() {
  if (typeof window === 'undefined') return null

  const match = window.location.pathname.match(LINK_INVITE_PATH_PATTERN)
  if (!match?.[1]) return null

  try {
    const trimmed = decodeURIComponent(match[1]).trim()
    return trimmed !== '' ? trimmed : null
  } catch {
    return null
  }
}

/**
 * @param {(typeof AUTH_SCOPE)[keyof typeof AUTH_SCOPE]} authScope
 * @param {EmailAccessSession | LinkAccessSession} session
 */
function persistAccess(authScope, session) {
  sessionStorageService.set(QUERY_KEYS[authScope], session)
}

/**
 * Strips bearer + grants but keeps email / link code (401/403 and pre-relogin).
 *
 * @param {(typeof AUTH_SCOPE)[keyof typeof AUTH_SCOPE]} authScope
 */
function clearAccessCredentials(authScope) {
  const session = readStoredSession(authScope)
  if (!session) return

  if (authScope === AUTH_SCOPE.LINK) {
    persistAccess(authScope, {
      bearerToken: null,
      grants: {},
      linkCode: session.linkCode,
    })
    return
  }

  persistAccess(authScope, {
    bearerToken: null,
    grants: {},
    email: session.email,
  })
}

/**
 * @param {(typeof AUTH_SCOPE)[keyof typeof AUTH_SCOPE]} authScope
 */
function clearAccess(authScope) {
  sessionStorageService.remove(QUERY_KEYS[authScope])
}

/**
 * @param {(typeof AUTH_SCOPE)[keyof typeof AUTH_SCOPE]} authScope
 * @returns {string | null}
 */
function getBearerToken(authScope) {
  return getAccess(authScope)?.bearerToken ?? null
}

/**
 * Reads the stored session blob. Returns `null` for missing / malformed entries.
 * Validity (JWT exp, grants, bearer presence) is checked separately in
 * {@link isValidAccessSession}; relogin paths read the raw blob so `email` /
 * `linkCode` remain available after the JWT expires.
 *
 * @param {(typeof AUTH_SCOPE)[keyof typeof AUTH_SCOPE]} authScope
 * @returns {EmailAccessSession | LinkAccessSession | null}
 */
function readStoredSession(authScope) {
  const session = sessionStorageService.get(QUERY_KEYS[authScope])
  if (!session || typeof session !== 'object' || Array.isArray(session)) {
    return null
  }
  return session
}

/** @param {EmailAccessSession | LinkAccessSession} session */
function isValidAccessSession(session) {
  return (
    !!session.bearerToken &&
    hasValidGrants(session.grants) &&
    !isBearerTokenExpired(session.bearerToken)
  )
}
